import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { AppShell, PageHeader, Section } from '../../../components/layout';
import { Card, Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';

export default function CourseShow({ student, course, units, books, next_lesson }) {
  const { t } = useTranslation();
  return (
    <AppShell student={student} scrollTitle={<><Link href="/student/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400">{t('nav.dashboard')}</Link>{'  ›  '}{course.title}</>}>
      <div className="max-w-4xl mx-auto">
        <PageHeader
          backLink="/student/dashboard"
          backLabel={t('nav.dashboard')}
          title={course.title}
          subtitle={course.description}
        />

        {course.date_range && (
          <p className="text-sm text-gray-500 dark:text-gray-400 -mt-4 mb-8 flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" />
            {course.date_range}
          </p>
        )}

        {/* Up Next CTA */}
        {next_lesson && (
          <Card gradient elevated className="mb-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-gray-100 dark:from-gray-700 to-transparent rounded-bl-full opacity-50" />
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  {t('studentCourse.upNext')}
                </p>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {next_lesson.lesson_title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {next_lesson.unit_title}
                </p>
              </div>
              <Link href={next_lesson.practice_url}>
                <Button variant="primary" size="md">
                  {t('common.continue')}
                  <svg className="ml-1.5 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Units and Lessons */}
        {units.map((unit) => {
          const progressLine = unit.total_lesson_count > 0
            ? t('studentCourse.unitProgress', {
                completed: unit.completed_lesson_count,
                count: unit.total_lesson_count,
              })
            : null;

          return (
            <Section
              key={unit.id}
              title={unit.title}
              description={unit.description}
              action={progressLine && (
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                  {progressLine}
                </span>
              )}
            >
              {unit.lessons.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
                  {t('studentCourse.noLessonsYet')}
                </p>
              ) : (
                <Card padding="none">
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {unit.lessons.map((lesson) => (
                      <LessonRow key={lesson.id} lesson={lesson} />
                    ))}
                  </div>
                </Card>
              )}
            </Section>
          );
        })}

        {units.length === 0 && (
          <Card className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('studentCourse.noUnitsYet')}
            </p>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

const STATUS_CONFIG = {
  completed: {
    iconColor: 'text-green-500',
    labelKey: 'common.completed',
    labelClass: 'text-green-600 dark:text-green-400',
  },
  in_progress: {
    iconColor: 'text-blue-500',
    labelClass: 'text-blue-600 dark:text-blue-400',
  },
  not_started: {
    iconColor: 'text-gray-300 dark:text-gray-600',
    labelClass: 'text-gray-500 dark:text-gray-400',
  },
  no_activities: {
    iconColor: 'text-gray-200 dark:text-gray-700',
    labelKey: 'studentCourse.comingSoon',
    labelClass: 'text-gray-400 dark:text-gray-500 italic',
  },
};

function LessonRow({ lesson }) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[lesson.status] || STATUS_CONFIG.not_started;

  const label = config.labelKey
    ? t(config.labelKey)
    : (lesson.status === 'in_progress'
      ? `${lesson.completed_count}/${lesson.activity_count}`
      : `${lesson.activity_count} ${lesson.activity_count === 1 ? t('common.activity') : t('common.activities')}`);

  const content = (
    <div className={cn(
      'px-5 py-3.5 flex items-center gap-3',
      lesson.url && 'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer'
    )}>
      <div className="flex-shrink-0">
        <StatusIcon status={lesson.status} className={cn('w-5 h-5', config.iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {lesson.title}
        </p>
        <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {lesson.lesson_type && (
            <span className="capitalize">{lesson.lesson_type.replace('_', ' ')}</span>
          )}
          {lesson.duration_minutes && <span>{lesson.duration_minutes} {t('common.min')}</span>}
        </div>
      </div>
      <span className={cn('text-xs font-medium flex-shrink-0', config.labelClass)}>
        {label}
      </span>
      {lesson.url && (
        <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  );

  if (lesson.url) {
    return <Link href={lesson.url}>{content}</Link>;
  }
  return content;
}

function StatusIcon({ status, className }) {
  if (status === 'completed') {
    return (
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    );
  }
  if (status === 'in_progress') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
